<template>
  <v-row no-gutters class="pa-4" style="background-color: white; height: 212px;">
    <v-col>
      <dx-chart v-if="chartData.length" id="orders-chart" :data-source="chartData" :palette="'Material'"
        :resolve-label-overlapping="'shift'" style="height: 180px;">
        <!-- X axis: date -->
        <dx-argument-axis argument-type="datetime" :value-margins-enabled="false" :grid="{ visible: true }" />

        <!-- Y axis: amount -->
        <dx-value-axis :grid="{ visible: true }" :tick-interval="chartTickInterval" />

        <!-- common settings -->
        <dx-common-series-settings type="line" argument-field="date" />

        <!-- solid line: past orders -->
        <dx-series value-field="pastAmount" name="Past orders" :point="{ size: 5 }" />

        <!-- dashed line: last past -> next order -->
        <dx-series v-if="hasNextLink" value-field="linkAmount" name="Next order" dash-style="dash"
          :point="{ size: 0 }" />

        <dx-tooltip :enabled="true" :customize-tooltip="customizeTooltip" />

        <dx-legend vertical-alignment="top" horizontal-alignment="right" />
      </dx-chart>

      <div v-else class="py-2 px-3 grey--text text--darken-1">
        No data to display
      </div>
    </v-col>
  </v-row>
</template>

<script>

import {
  DxChart,
  DxSeries,
  DxArgumentAxis,
  DxValueAxis,
  DxLegend,
  DxTooltip,
  DxCommonSeriesSettings,
} from 'devextreme-vue/chart';

export default {
  name: 'ContractOrdersGraph',
  props: {
    // Array of plain order objects: { CreatedDate, TotalAmountExclVat, ... }
    pastOrders: {
      type: Array,
      default: () => [],
    },
    // Plain order object or null
    nextOrders: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    DxChart,
    DxSeries,
    DxArgumentAxis,
    DxValueAxis,
    DxLegend,
    DxTooltip,
    DxCommonSeriesSettings,
  },

  methods: {
    customizeTooltip(arg) {
      //const d = arg.argument;             // x value (Date)
      const amount = arg.originalValue;     // y value
      const data = arg.point.data || {};    // chartData row
      return {
        text: `${data.orderIdText}\n ${this.formatCurrencyTotal(amount)}`,
      };
    },
  },

  computed: {
    lastPastOrder() {
      if (!this.pastOrders.length) return null;
      // assumes sorted by CreatedDate asc, otherwise sort here
      return this.pastOrders[this.pastOrders.length - 1];
    },

    hasNextLink() {
      return !!(this.lastPastOrder && this.nextOrders);
    },

    chartData() {
      const previousValues = this.pastOrders.reduce((acc, item) => {
        const date = new Date(item.CreatedDate);
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // getMonth() returns 0-11, so add 1 for 1-12
        const key = `${year}-${month.toString().padStart(2, '0')}`; // Format as YYYY-MM

        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key] += item.TotalAmountExclVat;
        return acc;
      }, {})
      
      var previousResult = Object.keys(previousValues).map(date => {
        return {
          date: date,
          orderIdText: `Total for ${date}`,
          pastAmount: previousValues[date],
        };
      });

      const nextValues = this.nextOrders.reduce((acc, item) => {
        const date = new Date(item.CreatedDate);
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // getMonth() returns 0-11, so add 1 for 1-12
        const key = `${year}-${month.toString().padStart(2, '0')}`; // Format as YYYY-MM

        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key] += item.TotalAmountExclVat;
        return acc;
      }, {})

      var nextResult = Object.keys(nextValues).map(date => {
        return {
          date: date,
          orderIdText: `Total for ${date}`,
          linkAmount: nextValues[date],
        };
      });


      const fillMonths = getMonthsBetweenDates(new Date(Object.keys(previousValues).at(0)), new Date(Object.keys(nextValues).at(-1)));

      if (fillMonths.length <= 2) {
        return [...previousResult, ...nextResult];
      }

      console.table(fillMonths.slice(1,-1));
      var fillResults = fillMonths.slice(1, -1).map(date => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const key = `${year}-${month}`;
        return {
          date: key,
          orderIdText: `(No orders for ${key})`,
          pastAmount: 0,
          linkAmount: 0,
        };
      });
      return [...previousResult, ...fillResults, ...nextResult];
    },

    chartTickInterval() {
      if (!this.chartData.length) return undefined;
      const max = Math.max(
        ...this.chartData.map(r =>
          Math.max(r.pastAmount || 0, r.linkAmount || 0)
        )
      );
      const value = max > 0 ? Math.round(max / 5) : 0;
      return value || undefined;
    },
  },

  watch: {},
};
function getMonthsBetweenDates(startDate, endDate) {
  const months = [];
  let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1); // Start at the beginning of the first month

  while (currentDate <= endDate) {
    months.push(new Date(currentDate)); // Push a copy to avoid mutation issues
    currentDate.setMonth(currentDate.getMonth() + 1); // Move to the next month
  }

  return months;
}
</script>